import { useState, useCallback } from "react";
import {
  GlEntity,
  GlPaginationEntity,
  GlOperationResult,
  AccountTypeGroupsEntity,
} from "../../domain";
import { GlRepositoryImpl } from "../../data/repositories/GlRepositoryImpl";
import { GetGlsUseCase } from "../../domain/use-cases/GetGlsUseCase";
import { CreateGlUseCase } from "../../domain/use-cases/CreateGlUseCase";
import { UpdateGlUseCase } from "../../domain/use-cases/UpdateGlUseCase";
import { DeleteGlUseCase } from "../../domain/use-cases/DeleteGlUseCase";
import { GetAccountTypeGroupsUseCase } from "../../domain/use-cases/GetAccountTypeGroupsUseCase";

const glRepository = new GlRepositoryImpl();
const getGlsUseCase = new GetGlsUseCase(glRepository);
const createGlUseCase = new CreateGlUseCase(glRepository);
const updateGlUseCase = new UpdateGlUseCase(glRepository);
const deleteGlUseCase = new DeleteGlUseCase(glRepository);
const getAccountTypeGroupsUseCase = new GetAccountTypeGroupsUseCase(glRepository);

const PAGE_SIZE = 20;

export interface UseGlsReturn {
  gls: GlEntity[];
  pagination: GlPaginationEntity | null;
  loading: boolean;
  error: string | null;
  accountTypeGroups: AccountTypeGroupsEntity | null;
  accountTypeGroupsLoading: boolean;
  fetchGls: (page?: number, search?: string) => Promise<void>;
  createGl: (payload: {
    account_name: string;
    account_type: string;
    parent_account_id?: string | null;
    invoice?: boolean;
    bill?: boolean;
    node_kind?: "ledger" | "group";
    request_comments?: string;
  }) => Promise<GlOperationResult>;
  updateGl: (
    accountId: string,
    payload: {
      account_name?: string;
      account_type?: string;
      parent_account_id?: string | null;
      invoice?: boolean;
      bill?: boolean;
      node_kind?: "ledger" | "group";
      request_comments?: string;
    }
  ) => Promise<GlOperationResult>;
  deleteGl: (
    accountId: string,
    payload?: { request_comments?: string }
  ) => Promise<GlOperationResult>;
  getAccountTypeGroups: () => Promise<AccountTypeGroupsEntity>;
  /** Load GLs by account type for parent dropdown */
  loadParentGlOptions: (accountType: string, search?: string) => Promise<GlEntity[]>;
  /** Apply edited GL fields directly in the list without a network refresh */
  updateGlLocally: (updated: GlEntity) => void;
  /** Remove a GL from the list without a network refresh */
  deleteGlLocally: (accountId: string) => void;
  /** Mark a GL as under-review in the list without a network refresh */
  markGlUnderReview: (accountId: string) => void;
}

export function useGls(): UseGlsReturn {
  const [gls, setGls] = useState<GlEntity[]>([]);
  const [pagination, setPagination] = useState<GlPaginationEntity | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [accountTypeGroups, setAccountTypeGroups] = useState<AccountTypeGroupsEntity | null>(null);
  const [accountTypeGroupsLoading, setAccountTypeGroupsLoading] = useState(false);

  const fetchGls = useCallback(async (page: number = 1, search?: string) => {
    setLoading(true);
    setError(null);
    try {
      const offset = (page - 1) * PAGE_SIZE;
      const result = await getGlsUseCase.execute(PAGE_SIZE, offset, search, true);
      setGls(result.gls);
      setPagination(result.pagination);
    } catch (e) {
      const message = e instanceof Error ? e.message : "Failed to fetch GLs";
      setError(message);
      setGls([]);
      setPagination(null);
    } finally {
      setLoading(false);
    }
  }, []);

  const createGl = useCallback(
    async (payload: {
      account_name: string;
      account_type: string;
      parent_account_id?: string | null;
      invoice?: boolean;
      bill?: boolean;
      node_kind?: "ledger" | "group";
      request_comments?: string;
    }) => {
      return createGlUseCase.execute(payload);
    },
    []
  );

  const updateGl = useCallback(
    async (
      accountId: string,
      payload: {
        account_name?: string;
        account_type?: string;
        parent_account_id?: string | null;
        invoice?: boolean;
        bill?: boolean;
        node_kind?: "ledger" | "group";
        request_comments?: string;
      }
    ) => {
      return updateGlUseCase.execute(accountId, payload);
    },
    []
  );

  const deleteGl = useCallback(
    async (accountId: string, payload?: { request_comments?: string }) => {
      return deleteGlUseCase.execute(accountId, payload);
    },
    []
  );

  const getAccountTypeGroups = useCallback(async () => {
    setAccountTypeGroupsLoading(true);
    try {
      const result = await getAccountTypeGroupsUseCase.execute();
      setAccountTypeGroups(result);
      return result;
    } finally {
      setAccountTypeGroupsLoading(false);
    }
  }, []);

  const loadParentGlOptions = useCallback(
    async (accountType: string, search?: string) => {
      const result = await glRepository.fetchGls({
        limit: 200,
        offset: 0,
        search,
        account_type: accountType,
      });
      return result.gls;
    },
    []
  );

  const updateGlLocally = useCallback((updated: GlEntity) => {
    setGls((prev) =>
      prev.map((g) => (g.accountId === updated.accountId ? updated : g))
    );
  }, []);

  const deleteGlLocally = useCallback((accountId: string) => {
    setGls((prev) => prev.filter((g) => g.accountId !== accountId));
    setPagination((prev) =>
      prev ? { ...prev, total: Math.max(0, prev.total - 1) } : prev
    );
  }, []);

  const markGlUnderReview = useCallback((accountId: string) => {
    setGls((prev) =>
      prev.map((g) =>
        g.accountId === accountId ? { ...g, isUnderReview: true } : g
      )
    );
  }, []);

  return {
    gls,
    pagination,
    loading,
    error,
    accountTypeGroups,
    accountTypeGroupsLoading,
    fetchGls,
    createGl,
    updateGl,
    deleteGl,
    getAccountTypeGroups,
    loadParentGlOptions,
    updateGlLocally,
    deleteGlLocally,
    markGlUnderReview,
  };
}
